'use client';
import { MouseEvent, useRef } from 'react';
import {
  ReactZoomPanPinchRef,
  TransformWrapper,
  TransformComponent
} from 'react-zoom-pan-pinch';

import cn from 'classnames';
import Image from 'next/image';

import {
  PRODUCT_DETAIL_IMG_HEIGHT,
  PRODUCT_DETAIL_IMG_WIDTH
} from '@/const/product';

import './ProductZoomImage.style.scss';

type ProductZoomImageProps = {
  altText: string;
  className?: string;
  imageSrc: string;
  scale?: number;
};

function ProductZoomImage(props: ProductZoomImageProps) {
  const { altText, className = '', imageSrc, scale = 2 } = props;

  const transformComponentRef = useRef<ReactZoomPanPinchRef | null>(null);

  const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
    if (transformComponentRef.current) {
      const { setTransform } = transformComponentRef.current;
      const containerElement = e.currentTarget.parentElement;
      const imageElement = e.currentTarget
        .querySelector('img')
        ?.getBoundingClientRect();

      if (!imageElement || !containerElement) return;

      // use clientX/Y instead of pageX/Y; we want to measure from the
      // viewport edge, not the top of the (potentially scrolled) page
      const left = Math.max(
        Math.min(e.clientX - imageElement.left, imageElement.width),
        0
      );
      const top = Math.max(
        Math.min(e.clientY - imageElement.top, imageElement.height),
        0
      );

      // Magnify and inverse the image movement relative to mouse movement
      const x = (left / scale) * -1;
      const y = (top / scale) * -1;

      // `easeInQuint` seems to speed up the initial zoom so it's not _so_ glitchy
      setTransform(x, y, scale, 0, 'easeInQuint');
    }
  };

  const handleMouseLeave = () => {
    if (transformComponentRef.current) {
      const { resetTransform } = transformComponentRef.current;

      resetTransform();
    }
  };

  return (
    <TransformWrapper
      disablePadding={true}
      doubleClick={{
        disabled: true
      }}
      wheel={{
        disabled: true
      }}
      ref={transformComponentRef}
    >
      <TransformComponent>
        <div
          className='ProductZoomImage'
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <Image
            className='ProductZoomImage-img'
            src={imageSrc as string}
            alt={altText}
            height={PRODUCT_DETAIL_IMG_HEIGHT}
            width={PRODUCT_DETAIL_IMG_WIDTH}
          />
        </div>
      </TransformComponent>
    </TransformWrapper>
  );
}

export default ProductZoomImage;
